import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import type { Permission, Role } from '@dt-pm/core';
import { Spinner } from '@/design-system';
import { useAuth } from '@/app/providers/AuthProvider';
import { ROUTES } from './routes';

function FullScreenLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <Spinner size="lg" className="text-brand-accent" />
    </div>
  );
}

/** Exige sessao valida; guarda a rota de origem para voltar apos o login. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to={ROUTES.login} state={{ from: location }} replace />;

  return children ? <>{children}</> : <Outlet />;
}

/** Restringe a rota por papel e/ou permissao (PRJ-020). */
export function RequireRole({
  roles,
  permission,
  children,
}: {
  roles?: Role[];
  permission?: Permission;
  children?: ReactNode;
}) {
  const { user, isLoading, hasPermission } = useAuth();

  if (isLoading) return <FullScreenLoader />;
  if (!user) return <Navigate to={ROUTES.login} replace />;

  const roleAllowed = !roles || roles.includes(user.role);
  const permissionAllowed = !permission || hasPermission(permission);

  if (!roleAllowed || !permissionAllowed) return <Navigate to={ROUTES.forbidden} replace />;

  return children ? <>{children}</> : <Outlet />;
}

/** Impede que um usuario logado volte para o login. */
export function RedirectIfAuthenticated({ children }: { children?: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <FullScreenLoader />;
  if (isAuthenticated) return <Navigate to={ROUTES.home} replace />;

  return children ? <>{children}</> : <Outlet />;
}
